import { useLayout, useLocation, useMap, useOrgSettings } from "@/services";
import { ILocation } from "@/types";
import { GeoJSONSource, Map } from "mapbox-gl";
import bbox from "@turf/bbox";
import { calcLength, emptyGeoJSON, meterSquaredToAcres } from "@/util/helpers";
import { area, Feature, FeatureCollection } from "@turf/turf";
import { lineString } from "@turf/helpers";
import { GeoJsonProperties, Geometry } from "geojson";

export default function useRiskGeometry() {
  const parameterLayerName = "risks-geometry-parameter-measurement";
  const areaLayerName = "risks-geometry-area-measurement";

  function init(map: Map) {
    map.on("draw.update", () => apply());
    map.on("draw.create", () => apply());
    map.on("draw.modechange", (e) => {
      const { setIsEditingRiskGeom, setEditingMode, isEditingRiskGeom } =
        useMap();
      const { mapDraw, isDrawingRiskGeom } = useMap();
      if (!mapDraw.getAll()?.features?.length && isDrawingRiskGeom) {
        endDraw();
      }

      if (isEditingRiskGeom && e.mode === "simple_select") {
        setEditingMode({ mode: "simple_select" }, true);
        setIsEditingRiskGeom(false);
      }
    });
  }

  function loadMeasurementLayers(map: Map) {
    if (useOrgSettings().applicationSettings?.showMeasuresOnGeometryRisk) {
      addGeomMeasurementLayer();

      map.on("draw.drawingUpdate", (e) => {
        if (e.features[0]) updateMeasurementsFeatures(e.features[0]);
      });
    }
  }

  function addGeomMeasurementLayer(): void {
    const { map, mapDraw } = useMap();
    if (!map) return;
    map.addSource(parameterLayerName, {
      type: "geojson",
      data: emptyGeoJSON,
    });

    map.addSource(areaLayerName, {
      type: "geojson",
      data: emptyGeoJSON,
    });

    const paint = {
      "text-color": "#626262",
      "text-halo-color": "#F2F2F0",
      "text-halo-blur": 0,
      "text-halo-width": 10,
    };

    map.addLayer({
      id: "risks-geometry-line-distance",
      type: "symbol",
      source: parameterLayerName,
      layout: {
        "text-field": "{distance}",
        "text-font": ["Source Sans Pro Regular"],
        "text-size": 18,
        "symbol-placement": "line-center",
        "text-offset": [0, 0.75],
      },
      paint,
    });

    map.addLayer({
      id: "risks-geometry-area-measurement",
      type: "symbol",
      source: areaLayerName,
      layout: {
        "text-field": "{geomArea}",
        "text-font": ["Source Sans Pro Regular"],
        "text-size": 18,
      },
      paint,
    });

    const drawGeojson = mapDraw?.getAll();
    if (drawGeojson?.features.length) {
      updateMeasurementsFeatures(drawGeojson.features[0].geometry);
    }
  }

  function updateMeasurementsFeatures(geom: any) {
    if (
      !geom?.coordinates?.length ||
      !useOrgSettings().applicationSettings?.showMeasuresOnGeometryRisk
    )
      return;
    const lines = [...(geom.coordinates[0] as any[])].slice(1).map((x, i) => {
      const line = lineString([geom.coordinates[0][i], x]);
      line.properties = {
        distance: calcLength(line),
      };
      return line;
    });

    const areaInMSquared = area(geom);
    const areaInAcres = meterSquaredToAcres(areaInMSquared);
    updateMeasurementsData(
      {
        type: "FeatureCollection",
        features: lines,
      },
      {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: geom.coordinates,
        },
        properties: {
          geomArea: `Area: ${areaInMSquared.toFixed(
            2
          )}m2 (${areaInAcres.toFixed(2)}ac)`,
        },
      }
    );
  }

  function updateMeasurementsData(
    measurementLines: FeatureCollection<Geometry, GeoJsonProperties>,
    geomArea:
      | Feature<Geometry, GeoJsonProperties>
      | FeatureCollection<Geometry, GeoJsonProperties>
  ) {
    const { map, isDrawingRiskGeom } = useMap();
    if (
      !isDrawingRiskGeom ||
      !useOrgSettings().applicationSettings?.showMeasuresOnGeometryRisk
    )
      return;
    (map?.getSource(parameterLayerName) as GeoJSONSource)?.setData(
      measurementLines
    );

    (map?.getSource(areaLayerName) as GeoJSONSource)?.setData(geomArea);
  }

  function startDraw(): void {
    const map = useMap();
    const layout = useLayout();
    map.dataBoundary = null;
    layout.isDrawToolOpen = false;
    map.mapDraw.deleteAll();
    map.setEditingMode({ mode: "draw_risk_polygon" }, true);
  }

  function edit(): void {
    const map = useMap();
    map.setEditingMode({ mode: "direct_select" }, true);
    map.setIsEditingRiskGeom(true);
  }

  function endDraw() {
    const { mapDraw, setEditingMode, map } = useMap();
    if (!mapDraw) {
      return;
    }
    updateMeasurementsData(emptyGeoJSON, emptyGeoJSON);
    setEditingMode({ mode: "simple_select" }, false);
    useMap().dataBoundary = null;
    mapDraw.deleteAll();
    if (map) {
      map.getCanvas().style.cursor = "";
    }
  }

  async function apply(): Promise<void> {
    const { mapDraw, isDrawingRiskGeom } = useMap();
    if (!isDrawingRiskGeom) return;
    const feature = mapDraw?.getAll().features[0];
    updateMeasurementsFeatures(feature.geometry);
    useLocation().updateLocation({
      type: "Geometry",
      geometry: feature.geometry,
    });
  }

  async function set(location: ILocation, fitToScreen = false) {
    const { mapDraw, setEditingMode, map, isEditingRiskGeom } = useMap();
    if (isEditingRiskGeom) return;
    endDraw();
    setEditingMode({ mode: "simple_select" }, true);
    const shape = {
      type: "FeatureCollection",
      id: "some_ID",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: location.geometry,
        },
      ],
    };
    mapDraw.set(shape);
    if (location?.geometry) updateMeasurementsFeatures(location.geometry);

    if (fitToScreen) {
      const x = bbox(shape);
      map?.fitBounds(x as any, {
        padding: 100,
      });
    }
  }

  return { init, startDraw, endDraw, set, edit, loadMeasurementLayers };
}
